import { createAsyncThunk } from '@reduxjs/toolkit';

import api from '@setup/api';
import { handleServerError } from '@setup/config/serverErrorHandler';
import { StatusServerErrors } from '@shared/enums/status-server-errors';

import i18next from '../../setup/locales/i18n';

import type { AppDispatch } from '../store';
import type { PlannerItemStatusesEnum } from '@shared/enums/plannerItemStatuses.enum';
import type { ErrorType, ServerErrorType } from '@shared/interfaces/error.interfaces';
import type { AppointmentModel } from '@shared/models/appointment/appointment.model';
import type { CreateAppointmentFormPayloadModel } from '@shared/models/appointment/createAppointmentFormPayload.model';
import type { ItemUserModel } from '@shared/models/itemUser.model';

export const createAppointmentItem = createAsyncThunk<
  AppointmentModel,
  CreateAppointmentFormPayloadModel,
  { rejectValue: ErrorType; dispatch: AppDispatch }
>('createAppointmentItem', async (params, { rejectWithValue }) => {
  try {
    return await api.appointment.createAppointments(params);
  } catch (e) {
    const axiosError = e as ServerErrorType;
    handleServerError(e);
    return rejectWithValue(axiosError.data);
  }
});

export const updateAppointmentItem = createAsyncThunk<
  AppointmentModel,
  { params: CreateAppointmentFormPayloadModel; appointmentId: number; confirmation_status?: string },
  { rejectValue: ErrorType; dispatch: AppDispatch }
>('updateAppointmentItem', async ({ params, appointmentId, confirmation_status }, { rejectWithValue }) => {
  try {
    return await api.appointment.updateAppointment(params, appointmentId, confirmation_status);
  } catch (e) {
    const axiosError = e as ServerErrorType;
    handleServerError(e);
    return rejectWithValue(axiosError.data);
  }
});

export const updateUsersAppointment = createAsyncThunk<
  AppointmentModel,
  { users: ItemUserModel[]; appointmentId: number },
  { rejectValue: ErrorType; dispatch: AppDispatch }
>('updateUsersMeeting', async (params, { rejectWithValue }) => {
  try {
    return await api.appointment.updateUsersAppointment(params);
  } catch (e) {
    const axiosError = e as ServerErrorType;
    handleServerError(e);
    return rejectWithValue(axiosError.data);
  }
});

export const getAppointment = createAsyncThunk<
  AppointmentModel,
  { appointmentId: number; is_list?: number },
  { rejectValue: ErrorType; dispatch: AppDispatch }
>('getAppointment', async (props, { rejectWithValue }) => {
  try {
    return await api.appointment.getAppointment(props);
  } catch (e) {
    const axiosError = e as ServerErrorType;
    handleServerError(
      e,
      {
        [StatusServerErrors.AccessDenied]: {
          message: i18next.t('general.notifications.notAssignedToMeeting'),
        },
        [StatusServerErrors.ItemNotFound]: {
          message: i18next.t('general.notifications.appointmentNotFound'),
        },
      },
      {
        notificationVariant: 'info',
      },
    );

    return rejectWithValue(axiosError.data);
  }
});

export const deleteAppointment = createAsyncThunk<
  number,
  { appointmentId: number; confirmation_status?: string },
  { rejectValue: ErrorType; dispatch: AppDispatch }
>('deleteAppointment', async (params, { rejectWithValue }) => {
  try {
    return await api.appointment.deleteAppointment(params).then(() => params.appointmentId);
  } catch (e) {
    const axiosError = e as ServerErrorType;
    handleServerError(e);
    return rejectWithValue(axiosError.data);
  }
});

export const removeMyselfFromAppointment = createAsyncThunk<
  number,
  { appointmentId: number; confirmation_status?: string },
  { rejectValue: ErrorType; dispatch: AppDispatch }
>('removeMyselfFromAppointment', async (params, { rejectWithValue }) => {
  try {
    return await api.appointment.removeMyself(params).then(() => params.appointmentId);
  } catch (e) {
    const axiosError = e as ServerErrorType;
    handleServerError(e);
    return rejectWithValue(axiosError.data);
  }
});

export const archiveAppointment = createAsyncThunk<
  number,
  { appointmentId: number; confirmation_status?: string; is_full: boolean },
  { rejectValue: ErrorType; dispatch: AppDispatch }
>('archiveAppointment', async (params, { rejectWithValue }) => {
  try {
    return await api.appointment.archiveAppointment(params).then(() => params.appointmentId);
  } catch (e) {
    const axiosError = e as ServerErrorType;
    handleServerError(e);
    return rejectWithValue(axiosError.data);
  }
});

export const unArchiveAppointment = createAsyncThunk<
  number,
  { appointmentId: number; confirmation_status?: string; is_full: boolean },
  { rejectValue: ErrorType; dispatch: AppDispatch }
>('unArchiveAppointment', async (params, { rejectWithValue }) => {
  try {
    return await api.appointment.unArchiveAppointment(params).then(() => params.appointmentId);
  } catch (e) {
    const axiosError = e as ServerErrorType;
    handleServerError(e);
    return rejectWithValue(axiosError.data);
  }
});

export const changeAppointmentStatus = createAsyncThunk<
  AppointmentModel,
  {
    appointmentId: number;
    status: PlannerItemStatusesEnum;
    is_common?: boolean;
    confirmation_status?: string;
  },
  { rejectValue: ErrorType; dispatch: AppDispatch }
>('changeAppointmentStatus', async (params, { rejectWithValue }) => {
  try {
    return await api.appointment.changeStatus(params);
  } catch (e) {
    const axiosError = e as ServerErrorType;
    handleServerError(e);
    return rejectWithValue(axiosError.data);
  }
});
